import { useQuery } from "@tanstack/react-query";
import { getPresignedUrls } from "../utils/utilityFunctions";

/**
 * Resolves an array of stored S3 image/video URLs (private fmv-data bucket) to
 * short-lived presigned URLs so full-resolution media renders on display pages.
 *
 * - Returns the original list as a fallback while the request is in flight or on
 *   error, so something always renders.
 * - Order is preserved, so callers can keep using array indices.
 * - Cached via react-query keyed by the URL list, so the same gallery shown in
 *   multiple components only triggers one request. Presigned URLs are valid for
 *   6h on the backend; we treat them as fresh for 1h.
 *
 * Use this in any component that renders ORIGINAL-quality images. Listing-card
 * thumbnails should keep using the CDN (`getS3Thumb`) instead.
 */
export function useSignedImages(urls) {
  const list = Array.isArray(urls) ? urls.filter(Boolean) : [];

  const { data } = useQuery({
    queryKey: ["signedImages", list],
    queryFn: () => getPresignedUrls(list),
    enabled: list.length > 0,
    staleTime: 1000 * 60 * 60, // 1h
    gcTime: 1000 * 60 * 60,
  });

  return data || list;
}
